import {
  CalendarClock,
  CheckCircle2,
  CircleDollarSign,
  ClipboardCheck,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { formatDuration, formatTimestamp } from "./ExpertSessions";

const formatPaise = (paise) => {
  const value = Number(paise || 0) / 100;
  return `₹${value.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const label = (value) => String(value || "Not configured").replaceAll("_", " ");

export default function ExpertDashboard({ dashboard, onNavigate }) {
  const expert = dashboard.expert_context;
  const summary = dashboard.summary || {};
  const profile = dashboard.profile_completion || {};
  const settingsEnabled = dashboard.consultation_configured;
  const metrics = [
    [
      "new-requests",
      "New Consultation Requests",
      summary.new_requests,
      ClipboardCheck,
    ],
    [
      "accepted-requests",
      "Accepted Requests",
      summary.accepted_requests,
      CheckCircle2,
    ],
    [
      "upcoming-appointments",
      "Upcoming Appointments",
      summary.upcoming_appointments,
      CalendarClock,
    ],
    [
      "consultations-today",
      "Consultations Today",
      summary.consultations_today,
      CalendarClock,
    ],
    [
      "active-consultations",
      "Active Consultation",
      summary.active_consultations,
      CircleDollarSign,
    ],
    [
      "completed-consultations",
      "Completed Consultations",
      summary.completed_consultations,
      CheckCircle2,
    ],
    [
      "calculated-value",
      "Calculated Consultation Value",
      formatPaise(summary.calculated_consultation_value_paise),
      CircleDollarSign,
    ],
    [
      "calculated-entitlement",
      "Calculated Expert Entitlement",
      formatPaise(summary.calculated_expert_entitlement_paise),
      CircleDollarSign,
    ],
  ];

  return (
    <div className="space-y-8" data-testid="expert-dashboard-page">
      <section className="border-l-4 border-blue-900 bg-white p-6 shadow-sm sm:p-8">
        <p className="text-xs font-bold uppercase tracking-[0.1em] text-blue-900">
          Expert workspace
        </p>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold text-slate-900 sm:text-4xl">
              Welcome back, {expert.name}
            </h1>
            <p
              className="mt-2 text-sm text-slate-600"
              data-testid="expert-dashboard-display-id"
            >
              {expert.expert_display_id} · Operational view in Asia/Kolkata
            </p>
          </div>
          <div
            className="flex flex-wrap gap-2"
            data-testid="expert-dashboard-statuses"
          >
            <Status label="Verification" value={dashboard.verification} />
            <Status label="Account" value={dashboard.account_status} />
          </div>
        </div>
      </section>

      <section
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
        data-testid="expert-dashboard-metrics"
      >
        {metrics.map(([key, title, value, Icon]) => (
          <article
            className="border border-slate-200 bg-white p-5 shadow-sm"
            data-testid={`expert-metric-${key}`}
            key={key}
          >
            <div className="flex items-start justify-between gap-3">
              <p className="max-w-[13rem] text-sm font-medium text-slate-600">
                {title}
              </p>
              <Icon
                className="h-5 w-5 shrink-0 text-blue-900"
                strokeWidth={1.5}
              />
            </div>
            <p className="mt-5 font-display text-2xl font-bold text-slate-900">
              {value ?? 0}
            </p>
          </article>
        ))}
      </section>

      <section className="grid gap-6 lg:grid-cols-12">
        <div className="space-y-6 lg:col-span-7">
          <SectionTitle
            title="Today / Next Action"
            subtitle="Prioritized from your current workflow."
          />
          <div
            className="border border-slate-200 bg-white shadow-sm"
            data-testid="expert-next-actions"
          >
            {dashboard.next_actions?.length ? (
              dashboard.next_actions.map((action) => (
                <button
                  className="flex w-full items-center justify-between gap-4 border-b border-slate-100 p-5 text-left last:border-b-0 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:ring-inset"
                  data-testid={`expert-next-action-${action.key}`}
                  key={action.key}
                  onClick={() => onNavigate(action.target)}
                  type="button"
                >
                  <span>
                    <span className="block font-semibold text-slate-900">
                      {action.title}
                    </span>
                    <span className="mt-1 block text-sm text-slate-600">
                      {action.detail}
                    </span>
                  </span>
                  <span className="text-sm font-semibold text-blue-900">
                    View
                  </span>
                </button>
              ))
            ) : (
              <EmptyMessage
                title="Nothing needs your attention right now"
                body="New requests, appointments, and ready consultations will appear here."
                testId="expert-next-actions-empty"
              />
            )}
          </div>

          <SectionTitle
            title="Upcoming Appointments"
            subtitle="Your next confirmed consultation commitments."
          />
          <div
            className="space-y-3"
            data-testid="expert-dashboard-upcoming-list"
          >
            {dashboard.upcoming_appointments?.length ? (
              dashboard.upcoming_appointments.map((appointment) => (
                <article
                  className="border border-slate-200 bg-white p-5 shadow-sm"
                  data-testid={`expert-dashboard-appointment-${appointment.appointment_id}`}
                  key={appointment.appointment_id}
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <p className="font-semibold text-slate-900">
                        {appointment.service_snapshot?.name || "Consultation"}
                      </p>
                      <p className="mt-1 text-sm text-slate-600">
                        {formatTimestamp(appointment.scheduled_start_utc)} ·{" "}
                        {appointment.society_name}
                      </p>
                    </div>
                    <span className="bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-800">
                      {label(appointment.status)}
                    </span>
                  </div>
                  <Button
                    className="mt-4"
                    data-testid={`expert-dashboard-appointment-view-${appointment.appointment_id}`}
                    onClick={() => onNavigate("appointments")}
                    size="sm"
                    type="button"
                    variant="outline"
                  >
                    View appointment
                  </Button>
                </article>
              ))
            ) : (
              <EmptyMessage
                title="No upcoming appointments"
                body="Confirmed appointments will appear here when they are scheduled."
                testId="expert-dashboard-upcoming-empty"
              />
            )}
          </div>
        </div>

        <div className="space-y-6 lg:col-span-5">
          <SectionTitle
            title="Profile Status"
            subtitle="A compact readiness check for your consultation work."
          />
          <article
            className="border border-slate-200 bg-white p-6 shadow-sm"
            data-testid="expert-profile-status"
          >
            <dl className="space-y-4 text-sm">
              <StatusRow
                label="Profile completeness"
                value={`${profile.percentage || 0}%`}
              />
              <StatusRow
                label="Verification"
                value={label(dashboard.verification)}
              />
              <StatusRow
                label="Account"
                value={label(dashboard.account_status)}
              />
              <StatusRow
                label="Expertise"
                value={`${dashboard.expertise_count || 0} active`}
              />
              {dashboard.expertise?.length > 0 && (
                <StatusRow
                  label="Categories"
                  value={dashboard.expertise.join(", ")}
                />
              )}
              <StatusRow
                label="Consultation availability"
                value={settingsEnabled ? "Enabled" : "Disabled"}
              />
              <StatusRow
                label="Availability"
                value={
                  dashboard.availability_configured
                    ? "Configured"
                    : "Not configured"
                }
              />
            </dl>
            <div className="mt-6 flex flex-wrap gap-2">
              <Button
                data-testid="expert-profile-status-view"
                onClick={() => onNavigate("profile")}
                size="sm"
                type="button"
              >
                View Profile
              </Button>
              <Button
                data-testid="expert-profile-status-edit"
                onClick={() => onNavigate("profile")}
                size="sm"
                type="button"
                variant="outline"
              >
                Edit Profile
              </Button>
              <Button
                data-testid="expert-profile-status-availability"
                onClick={() => onNavigate("availability")}
                size="sm"
                type="button"
                variant="outline"
              >
                Manage Availability
              </Button>
            </div>
          </article>

          <SectionTitle
            title="Consultation Pipeline"
            subtitle="Counts reflect existing operational states."
          />
          <div
            className="grid grid-cols-2 border border-slate-200 bg-white shadow-sm"
            data-testid="expert-pipeline"
          >
            {Object.entries(dashboard.pipeline || {}).map(([stage, count]) => (
              <div
                className="border-b border-r border-slate-100 p-4 even:border-r-0 last:border-b-0"
                data-testid={`expert-pipeline-${stage.toLowerCase()}`}
                key={stage}
              >
                <p className="text-xs font-bold uppercase tracking-[0.08em] text-slate-500">
                  {stage}
                </p>
                <p className="mt-2 font-display text-2xl font-bold text-slate-900">
                  {count}
                </p>
              </div>
            ))}
          </div>

          <SectionTitle
            title="Commercial Summary"
            subtitle="Stored calculation records only."
          />
          <article
            className="border border-slate-200 bg-white p-6 shadow-sm"
            data-testid="expert-commercial-summary"
          >
            <p
              className="border-l-4 border-slate-400 bg-slate-100 p-3 text-xs text-slate-700"
              data-testid="expert-commercial-summary-notice"
            >
              Amounts shown are calculated commercial entitlements and do not
              represent completed payment or payout unless separately confirmed.
            </p>
            <div className="mt-5 grid grid-cols-2 gap-4 text-sm">
              <StatusRow
                label="Completed commercial records"
                value={summary.commercial_records || 0}
              />
              <StatusRow
                label="Pending commercial calculation"
                value={summary.pending_commercial_calculation || 0}
              />
              <StatusRow
                label="Calculated Consultation Value"
                value={formatPaise(summary.calculated_consultation_value_paise)}
              />
              <StatusRow
                label="Calculated Expert Entitlement"
                value={formatPaise(summary.calculated_expert_entitlement_paise)}
              />
            </div>
            <Button
              className="mt-5"
              data-testid="expert-commercial-summary-view"
              onClick={() => onNavigate("commercial")}
              size="sm"
              type="button"
              variant="outline"
            >
              View Commercials
            </Button>
          </article>
        </div>
      </section>

      <section>
        <SectionTitle
          title="Recent Consultations"
          subtitle="Trusted session history with server-recorded timing."
        />
        <div
          className="space-y-3"
          data-testid="expert-dashboard-recent-consultations"
        >
          {dashboard.recent_consultations?.length ? (
            dashboard.recent_consultations.map((session) => (
              <article
                className="border border-slate-200 bg-white p-5 shadow-sm"
                data-testid={`expert-dashboard-session-${session.session_id}`}
                key={session.session_id}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <p className="font-semibold text-slate-900">
                      {session.display_id || session.session_id}
                    </p>
                    <p className="mt-1 text-sm text-slate-600">
                      {session.service_snapshot?.name || "Consultation"} ·{" "}
                      {session.society_name}
                    </p>
                  </div>
                  <span className="bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700">
                    {label(session.status)}
                  </span>
                </div>
                <div className="mt-4 grid gap-3 text-sm text-slate-600 sm:grid-cols-3">
                  <p>
                    <span className="text-slate-500">Actual start: </span>
                    {formatTimestamp(session.actual_start_utc)}
                  </p>
                  <p>
                    <span className="text-slate-500">Actual end: </span>
                    {formatTimestamp(session.actual_end_utc)}
                  </p>
                  <p>
                    <span className="text-slate-500">Duration: </span>
                    {formatDuration(session.actual_duration_seconds)}
                  </p>
                </div>
                {session.status === "ENDED" && (
                  <Button
                    className="mt-4"
                    data-testid={`expert-dashboard-session-commercial-${session.session_id}`}
                    onClick={() => onNavigate("commercial")}
                    size="sm"
                    type="button"
                    variant="outline"
                  >
                    View Commercial History
                  </Button>
                )}
              </article>
            ))
          ) : (
            <EmptyMessage
              title="No completed consultations yet"
              body="Trusted consultation history will appear after a consultation ends."
              testId="expert-recent-consultations-empty"
            />
          )}
        </div>
      </section>
    </div>
  );
}

function SectionTitle({ title, subtitle }) {
  return (
    <div>
      <h2 className="font-display text-xl font-bold text-slate-900">{title}</h2>
      <p className="mt-1 text-sm text-slate-600">{subtitle}</p>
    </div>
  );
}

function Status({ label: name, value }) {
  return (
    <span className="border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700">
      {name}: {label(value)}
    </span>
  );
}

function StatusRow({ label: name, value }) {
  return (
    <div>
      <dt className="text-xs text-slate-500">{name}</dt>
      <dd className="mt-1 font-semibold text-slate-900">{value}</dd>
    </div>
  );
}

function EmptyMessage({ title, body, testId }) {
  return (
    <div
      className="border border-dashed border-slate-200 bg-white p-6"
      data-testid={testId}
    >
      <p className="font-semibold text-slate-900">{title}</p>
      <p className="mt-1 text-sm text-slate-600">{body}</p>
    </div>
  );
}
